import {
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Text } from "@otrium/atoms";
import { Trans } from "@lingui/macro";
import { useTheme } from "@emotion/react";
import {
  ProductVariation,
  SingleProduct,
  SizeFitLabel,
} from "src/types/graphql.d";
import { Dropdown } from "src/atoms/DropDown";
import { MobileSizeOverlay } from "src/molecules/MobileSizeOverlay";
import { Overlay } from "src/molecules/Overlay";
import { getSizeStock, SizeMapping } from "src/hooks/useSizeSchemaFilter";
import { getChooseSizeText } from "src/organisms/ProductOrder/utils";
import { useLockBodyScroll } from "src/hooks/useLockBodyScroll";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { BackInStockNotification } from "src/molecules/BackInStockNotification";
import { ConversionItemAttributes } from "src/utils/constructor-beacon";

interface OldSizeSelectorProps {
  product: SingleProduct;
  selectedVariationId?: string;
  onSelectVariation: (variationId: string) => void;
  onAddToCart: () => Promise<boolean>;
  addToCartLoading: boolean;
  hasSizeGuide: boolean;
  onOpenSizeGuide?: () => void;
  dropdownHeader?: ReactNode;
  isSizeFitsSmall: boolean;
  hasError: boolean;
  disabled: boolean;
  defaultValue?: string;
  withOverlay: boolean;
  overlayExcludeRef?: RefObject<HTMLDivElement>;
  isMobile: boolean;
  sizeFitLabel?: SizeFitLabel | null;
  showSizeFitBanner: boolean;
  sizeFitBanner: React.ReactNode;
  onSizeSelectorOpen?: () => void;
  onSizeSelectorClose?: () => void;
  autoOpenSingleSize?: boolean;
  ariaDescribedBy?: string;
  dataTestId: string;
  onBackInStockPopupChange?: (isOpen: boolean) => void;
  cnstrcItem?: ConversionItemAttributes;
}

export const OldSizeSelector: FC<OldSizeSelectorProps> = ({
  product,
  selectedVariationId,
  onSelectVariation,
  onAddToCart,
  addToCartLoading,
  hasSizeGuide,
  onOpenSizeGuide,
  dropdownHeader,
  isSizeFitsSmall,
  hasError,
  disabled,
  defaultValue,
  withOverlay,
  overlayExcludeRef,
  isMobile,
  sizeFitLabel,
  showSizeFitBanner,
  sizeFitBanner,
  onSizeSelectorOpen,
  onSizeSelectorClose,
  autoOpenSingleSize,
  ariaDescribedBy,
  dataTestId,
  onBackInStockPopupChange,
  cnstrcItem,
}) => {
  const theme = useTheme();
  const { showOutOfStockVariants } = useFeatureFlags();
  const [mobileOverlayOpen, setMobileOverlayOpen] = useState(false);
  const [notifyPopupSizeMapping, setNotifyPopupSizeMapping] =
    useState<SizeMapping | null>(null);
  const [notifyPopupSelectedSize, setNotifyPopupSelectedSize] = useState("");

  useLockBodyScroll(isMobile && mobileOverlayOpen);

  const handleOpenMobileOverlay = useCallback(() => {
    onSizeSelectorOpen?.();
    setMobileOverlayOpen(true);
  }, [onSizeSelectorOpen]);

  const handleCloseMobileOverlay = useCallback(() => {
    setMobileOverlayOpen(false);
    onSizeSelectorClose?.();
  }, [onSizeSelectorClose]);

  const availableSizes = useMemo(() => {
    const variations =
      (product.variations as ProductVariation[] | undefined) || [];

    if (showOutOfStockVariants) {
      return variations;
    }

    return variations.filter((v) => (v.stock_quantity ?? 0) >= 1);
  }, [product.variations, showOutOfStockVariants]);

  useEffect(() => {
    if (autoOpenSingleSize && isMobile && !mobileOverlayOpen) {
      const inStockSizes = availableSizes.filter((v) => v.stock_quantity > 0);
      if (inStockSizes.length === 1 && !selectedVariationId) {
        onSelectVariation(inStockSizes[0].id);
      }
      handleOpenMobileOverlay();
    }
  }, [autoOpenSingleSize, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatSelectedSize = useCallback((variation: ProductVariation) => {
    const brandSize = variation.mapped_size?.value || variation.size || "";
    const cSize = variation.c_size;
    if (cSize) {
      const mappedSize = typeof cSize === "object" ? cSize.size : cSize;
      return `${brandSize} / ${mappedSize}`;
    }
    return brandSize;
  }, []);

  const handleDropdownChange = useCallback(
    (_event: any, { value }: { value: string }) => {
      const selectedVariation = availableSizes.find((v) => v.id === value);
      if (selectedVariation?.stock_quantity === 0) {
        if (!selectedVariationId) {
          onSelectVariation("");
        }
        setNotifyPopupSizeMapping({
          variationId: value,
          brandSize:
            selectedVariation.mapped_size?.value ||
            selectedVariation.size ||
            "",
          stockQuantity: 0,
          mappings: [],
        });
        setNotifyPopupSelectedSize(formatSelectedSize(selectedVariation));
        onBackInStockPopupChange?.(true);
        return;
      }
      onSelectVariation(value);
    },
    [
      onSelectVariation,
      availableSizes,
      formatSelectedSize,
      selectedVariationId,
      onBackInStockPopupChange,
    ]
  );

  const dropdownOptions = useMemo(
    () =>
      availableSizes.map(
        ({ id, size, c_size, stock_quantity, mapped_size }) => {
          const displaySize = mapped_size?.value || size || "";
          const isOutOfStock = stock_quantity === 0;
          return {
            key: id,
            disabled: false,
            className: isOutOfStock ? "out-of-stock" : undefined,
            text: product?.size_schema ? (
              <Text>
                {displaySize}
                <Text
                  as="span"
                  ml={1}
                  fontSize={theme.fontSizes[1]}
                  lineHeight={1.5}
                >
                  {product.size_schema}
                </Text>
                {c_size && (
                  <>
                    <Text
                      as="span"
                      ml={1}
                      mr={1}
                      fontSize={15}
                      lineHeight={1.5}
                    >
                      /
                    </Text>
                    <Text as="span">
                      {typeof c_size === "object" ? c_size.size : c_size}
                    </Text>
                    <Text
                      as="span"
                      ml={1}
                      fontSize={theme.fontSizes[1]}
                      lineHeight={1.5}
                    >
                      {typeof c_size === "object"
                        ? c_size.type || "INT"
                        : "INT"}
                    </Text>
                  </>
                )}
              </Text>
            ) : (
              <Text>{displaySize}</Text>
            ),
            value: id,
            description: isOutOfStock ? (
              <Text
                as="span"
                fontSize={theme.fontSizes[1]}
                fontWeight={600}
                sx={{
                  color: `${theme.colors.tone.black} !important`,
                }}
              >
                <Trans>Notify me</Trans>
              </Text>
            ) : stock_quantity <= 3 ? (
              getSizeStock(stock_quantity)
            ) : null,
            stockQuantity: stock_quantity,
            size: displaySize,
            mappedSize: c_size
              ? typeof c_size === "object"
                ? c_size.size || undefined
                : c_size
              : undefined,
          };
        }
      ),
    [availableSizes, product?.size_schema, theme.fontSizes]
  );

  const mobileOverlayContent = (
    <MobileSizeOverlay
      onClose={handleCloseMobileOverlay}
      openSizeGuide={onOpenSizeGuide}
      hasSizeGuide={hasSizeGuide}
      addToCartLoading={addToCartLoading}
      handleAddToCart={onAddToCart}
      cnstrcItem={cnstrcItem}
      options={dropdownOptions}
      selected={selectedVariationId}
      handleOption={handleDropdownChange}
      isSizeFitsSmall={isSizeFitsSmall}
      sizeFitLabel={
        showSizeFitBanner &&
        (sizeFitLabel === "runs_small" || sizeFitLabel === "runs_large")
          ? sizeFitLabel
          : undefined
      }
      product={product}
    />
  );

  return (
    <>
      <Dropdown
        data-testid={dataTestId}
        aria-describedby={ariaDescribedBy}
        aria-invalid={hasError}
        defaultValue={defaultValue}
        placeholder={getChooseSizeText(product?.size_schema)}
        header={isSizeFitsSmall ? dropdownHeader : undefined}
        value={selectedVariationId}
        options={dropdownOptions}
        onChange={!disabled ? handleDropdownChange : undefined}
        open={isMobile ? false : undefined}
        onClick={isMobile && !disabled ? handleOpenMobileOverlay : undefined}
        disabled={disabled}
        inlineMenu
      />

      {sizeFitBanner}

      {isMobile && withOverlay && (
        <Overlay
          isOpen={mobileOverlayOpen}
          isActive={mobileOverlayOpen}
          onClose={handleCloseMobileOverlay}
          excludeFromOutside={
            overlayExcludeRef ? [overlayExcludeRef] : undefined
          }
        >
          {mobileOverlayContent}
        </Overlay>
      )}

      {isMobile && !withOverlay && mobileOverlayOpen && mobileOverlayContent}

      {notifyPopupSizeMapping && (
        <BackInStockNotification
          isOpen={!!notifyPopupSizeMapping}
          onClose={() => {
            setNotifyPopupSizeMapping(null);
            onBackInStockPopupChange?.(false);
          }}
          product={product}
          sizeMapping={notifyPopupSizeMapping}
          selectedSize={notifyPopupSelectedSize}
          isMobile={isMobile}
        />
      )}
    </>
  );
};
